import axios from 'axios';
import { showToast } from './toastBus';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// The single navigation seam. Every full-page navigation in the app goes through
// here so it can be asserted in tests — jsdom has no real navigation, and stubbing
// `window.location` per test file diverges quickly.
//
// It must stay an `href` assignment: the tenancy flows rely on a genuine reload
// (App.js computes the '/' dashboard once at mount), and the 401 path's behaviour
// is a regression-guarded contract.
export const hardRedirect = (target) => {
  window.location.href = target;
};

// Add request interceptor to include JWT token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const clearSession = () => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
};

// Read straight from sessionStorage rather than importing authService: that
// module imports this one, so going the other way would be a cycle. That is also why
// the version literal is duplicated here rather than imported -- authService exports
// SESSION_SHAPE_VERSION, and importing it would be the cycle this comment exists to
// prevent. Keep the two in step.
const SESSION_SHAPE_VERSION = 2;

const currentRole = () => {
  try {
    const stored = JSON.parse(sessionStorage.getItem('user'));
    // A session from before ORG-08 (#303) describes a data model that no longer exists.
    // Returning null here is the honest answer, and the tenancy recovery below already
    // handles it as "not the platform admin".
    if (stored?.sessionVersion !== SESSION_SHAPE_VERSION) return null;
    return stored?.role ?? null;
  } catch {
    return null;
  }
};

const goTo = (target) => {
  // Never redirect to where we already are — the screens that render these
  // states (the picker, the suspended page) fetch on mount, so a redirect to
  // self would loop.
  if (window.location.pathname !== target) {
    hardRedirect(target);
  }
};

/**
 * Tenancy failures (plan §1.6) are never a silent logout. Each code has exactly
 * one correct recovery, and which one depends on whether the caller is the
 * platform admin — who can switch agencies — or an ordinary user, who cannot.
 *
 * Returns true when the error was a tenancy problem (handled or deliberately
 * ignored), so the caller can skip the legacy branches.
 */
const routeTenancyProblem = (status, data, url) => {
  const code = data?.code;
  if (!code || !String(code).startsWith('TENANT_')) return false;

  // /api/auth/* renders its own inline errors — Login shows the suspended copy,
  // the picker shows "no longer exists" / "still provisioning". Redirecting from
  // here would fight the screen already handling it. Note baseURL carries the
  // `/api` prefix, so config.url is '/auth/...' — matching '/api/auth/' never fires.
  if (String(url ?? '').startsWith('/auth/')) return true;

  const isPlatformAdmin = currentRole() === 'INITIAL_ADMIN';

  switch (code) {
    case 'TENANT_NOT_SELECTED':
      // Only the admin has anywhere to go. For anyone else this means a corrupt
      // token and there is no recovery to offer, so say so and leave the session
      // alone — logging them out would present a token problem as a credential
      // problem.
      if (isPlatformAdmin) {
        goTo('/select-agency');
      } else {
        showToast('Your session is missing its agency. Please sign out and back in.', 'error');
      }
      return true;

    case 'TENANT_SUSPENDED':
      // The credentials are fine, the agency is not — keep the session, or we'd
      // present an account problem as an auth problem. An INITIAL_ADMIN is
      // deliberately allowed into suspended tenants, so they are not routed.
      if (!isPlatformAdmin) goTo('/agency-suspended');
      return true;

    case 'TENANT_NOT_FOUND':
      // The admin's bound agency was deleted under them: back to the picker,
      // session intact. For anyone else their whole agency is gone, and there is
      // nothing left to be signed in to.
      if (isPlatformAdmin) {
        goTo('/select-agency');
      } else {
        clearSession();
        goTo('/login');
      }
      return true;

    case 'TENANT_NOT_READY':
      // Still provisioning. The picker renders the row's state and offers retry.
      return true;

    default:
      // TENANT_NOT_BOUND (500) and anything new: a server-side coding bug. Fail
      // loud rather than logging the user out over it.
      return true;
  }
};

/**
 * `responseType: 'blob'` applies to the whole request — ERROR RESPONSES INCLUDED.
 *
 * Verified in axios 1.13.6 (lib/defaults/index.js): the default transformResponse
 * JSON-parses only when `(forcedJSONParsing && !this.responseType) ||
 * this.responseType === 'json'`. So on a 403 from a viewer content endpoint,
 * `error.response.data` is a Blob, and two contracts break silently — serverMessage
 * finds neither `message` nor `detail` and falls back to generic copy, and
 * routeTenancyProblem's `data?.code` is undefined so a TENANT_ problem is never
 * routed. Un-blobbing here, ahead of every existing branch, keeps both working for
 * blob requests without either of them learning what a Blob is.
 */
const unblobProblemDetail = async (error) => {
  const data = error.response?.data;
  if (!(typeof Blob !== 'undefined' && data instanceof Blob)) return;
  // A ProblemDetail is application/problem+json; an empty type is what a body of
  // unknown type arrives as. Anything else (an HTML proxy error page, real bytes)
  // is left exactly as it came.
  if (!(data.type.includes('json') || data.type === '')) return;
  try {
    error.response.data = JSON.parse(await data.text());
  } catch {
    // Not JSON after all — leave the body untouched rather than losing it.
  }
};

// Add response interceptor to handle 401 errors and JWT errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    await unblobProblemDetail(error);
    if (error.response && routeTenancyProblem(error.response.status, error.response.data, error.config?.url)) {
      return Promise.reject(error);
    }
    // Handle 401 Unauthorized. /api/auth/* is exempt (#160): a 401 there means
    // "wrong credentials / wrong 2FA code", answered to a screen that renders it
    // inline — redirecting would reload the page and destroy that message before
    // it paints. Same match as the tenancy exemption above: baseURL carries
    // '/api', so config.url starts with '/auth/'.
    if (error.response && error.response.status === 401) {
      if (!String(error.config?.url ?? '').startsWith('/auth/')) {
        clearSession();
        hardRedirect('/login');
      }
    }
    // Handle 500 errors that might be JWT signature issues.
    //
    // /auth/* is exempt for the same reason the 401 branch above is, and here it is
    // load-bearing rather than tidy: this branch fires on the SUBSTRING 'token', and the
    // invitation endpoints' whole vocabulary is tokens. Without the exemption, any 500
    // from /auth/admin-invite/* whose message happened to contain that word would clear
    // the visitor's session and hard-redirect them off the accept page — discarding the
    // password they had just typed, and doing it to someone who has no session to lose.
    // The server-side copy avoids the word too ("invitation link"); this is the second
    // layer, because a message can change without anyone remembering this branch exists.
    else if (error.response && error.response.status === 500
             && !String(error.config?.url ?? '').startsWith('/auth/')) {
      const errorMessage = error.response.data?.message || error.message || '';
      // Check if it's a JWT-related error
      if (errorMessage.toLowerCase().includes('jwt') ||
          errorMessage.toLowerCase().includes('signature') ||
          errorMessage.toLowerCase().includes('token')) {
        console.error('JWT validation error detected, clearing session');
        clearSession();
        hardRedirect('/login');
      }
    }
    return Promise.reject(error);
  }
);

/**
 * One page of the document list as the server answers it: a Spring Page, `content` plus
 * `totalElements`, `last` and the rest, for a caller that pages ("Show more", DOC-05 #453).
 *
 * `folderId` lists one folder's own documents and `topLevel: true` the top level (DOC-05); with
 * neither it is every folder the reader may see, as the list always was. The server refuses a
 * `search` together with either, because search spans every folder.
 */
export const getDocumentsPage = async ({ search = '', folderId, topLevel, page, size, signal } = {}) => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (folderId) params.set('folderId', folderId);
  if (topLevel) params.set('topLevel', 'true');
  if (page != null) params.set('page', page);
  if (size != null) params.set('size', size);
  const qs = params.toString();
  const response = await api.get(`/documents${qs ? `?${qs}` : ''}`, { signal });
  return response.data;
};

// The rows only, every folder: the pickers and reports that read the list without paging it.
export const getDocuments = async (search = '', options = {}) =>
  (await getDocumentsPage({ ...options, search })).content;

// Full-text search over document title + extracted content, as the page the server answers:
// ranked rows (DocumentSearchResultResponse: list fields + score + highlight) plus the page
// fields. Spans every folder the reader may see.
export const searchDocumentsPage = async (q, options = {}) => {
  const params = new URLSearchParams();
  params.set('q', q);
  if (options.page != null) params.set('page', options.page);
  if (options.size != null) params.set('size', options.size);
  const response = await api.get(`/documents/search?${params.toString()}`, { signal: options.signal });
  return response.data;
};

// The ranked rows only.
export const searchDocuments = async (q, options = {}) => (await searchDocumentsPage(q, options)).content;

export const getDocumentById = async (id) => {
  const response = await api.get(`/documents/${id}`);
  return response.data;
};

// `folderId` files the document in that folder (DOC-06's field; the browser uploads into the
// folder on screen, DOC-05 #453); absent, it lands at the top level.
export const uploadDocument = async (file, minimumRankId, folderId) => {
  const formData = new FormData();
  formData.append('file', file);
  if (minimumRankId) {
    formData.append('minimumRankId', minimumRankId);
  }
  if (folderId) {
    formData.append('folderId', folderId);
  }
  const response = await api.post('/documents/upload', formData, {
    headers: { 'Content-Type': undefined },
  });
  return response.data;
};

/**
 * The document's raw bytes, for the in-app viewer.
 *
 * `responseType: 'blob'` is not optional: axios' default transformResponse
 * string-decodes a response body, so without it the bytes handed to
 * URL.createObjectURL are corrupt and the iframe paints nothing — with no error
 * anywhere. It also makes ERROR bodies Blobs, which the response interceptor
 * un-blobs (see unblobProblemDetail) so serverMessage and the tenancy routing
 * still work.
 *
 * The JWT rides in the Authorization header the request interceptor sets. There
 * is deliberately no ?token= form of this call: that would put a full-privilege
 * credential into nginx's access log on the public UAT origin and into browser
 * history (guarded by retiredExports.test.js).
 */
export const fetchDocumentContentBlob = async (id, { signal } = {}) => {
  const response = await api.get(`/documents/${id}/content`, { responseType: 'blob', signal });
  return response.data;
};

/**
 * The plain-text rendition of a non-PDF document (DocumentTextPreviewResponse:
 * documentId, fileName, contentType, text, truncated, extractionEmpty).
 *
 * Ordinary JSON, and the `text` is rendered as a React text node — never HTML.
 * That is the point: this feature exists partly to delete the app's only
 * dangerouslySetInnerHTML sink, so its replacement must not be one.
 */
export const getDocumentTextPreview = async (id, { signal } = {}) => {
  const response = await api.get(`/documents/${id}/preview-text`, { signal });
  return response.data;
};

export const getDocumentDownloadUrl = async (id) => {
  const response = await api.get(`/documents/${id}/download`);
  return response.data.url;
};

export const deleteDocument = async (id) => {
  await api.delete(`/documents/${id}`);
};

/**
 * Take a document out of circulation (ORG-27, #322).
 *
 * Archiving is what a document nobody may delete gets instead: a policy anyone has been
 * asked to acknowledge can never be removed, because the acknowledgement rows are the
 * record of what those people signed (decision D11, issue #179). It returns the updated
 * row, so a caller can re-render without a second read.
 */
export const archiveDocument = async (id) => {
  const response = await api.post(`/documents/${id}/archive`);
  return response.data;
};

/** Put an archived document back into circulation — AGENCY_ADMIN only (ORG-27, #322). */
export const unarchiveDocument = async (id) => {
  const response = await api.post(`/documents/${id}/unarchive`);
  return response.data;
};

/**
 * The archived documents, newest withdrawal first — AGENCY_ADMIN only (ORG-27, #322).
 *
 * The main list excludes archived documents unconditionally, so this is the one place
 * they can be found again.
 */
export const getArchivedDocumentsPage = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.page != null) params.set('page', options.page);
  if (options.size != null) params.set('size', options.size);
  const qs = params.toString();
  const response = await api.get(`/documents/archived${qs ? `?${qs}` : ''}`,
    { signal: options.signal });
  return response.data;
};

// The rows only.
export const getArchivedDocuments = async (options = {}) => (await getArchivedDocumentsPage(options)).content;

export default api;
